import { Header } from "@/layouts/Header";
import { Footer } from "@/layouts/Footer";
import { SEOHead } from "@/features/shared/SEOHead";

const Terms = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead
        title="Terms of Service | Frayze Technologies"
        description="Read Frayze's Terms of Service regarding your use of our websites, platform, and services."
        keywords="terms of service, tos, frayze terms, legal agreement, service conditions"
        canonical="https://frayze.ca/terms"
        robots="noindex, follow"
      />
      <Header />

      <main className="pt-40 pb-20 px-6">
        <div className="max-w-4xl mx-auto prose prose-slate dark:prose-invert">
          <h1>Terms of Service (Updated)</h1>
          <p className="text-muted-foreground">Effective Date: {currentDate}</p>

          <p>
            These Terms of Service (“Terms”) govern your access to and use of Frayze’s websites, platform and services (“Services”). By using the Services, you agree to these Terms.
          </p>

          <h2>1. Acceptance & Updates</h2>
          <p>
            Using our Services constitutes acceptance of these Terms. Do not use our Services if you disagree. We may update Terms periodically; your continued use signifies acceptance of changes.
          </p>

          <h2>2. Services Provided</h2>
          <p>
            Frayze offers AI‑powered CRM, marketing automation, websites/SEO, custom development, consulting and analytics. Subscription fees are charged per tier, and usage‑based billing applies to SMS, calls, emails, AI interactions and workflow automation. Third‑party integrations (e.g., Twilio, Meta, AWS, OpenAI, GHL) power many features; Frayze is not responsible for outages or data loss caused by these providers.
          </p>

          <h2>3. Eligibility & Account Responsibilities</h2>
          <p>
            You must be at least 18 years old or the legal age of majority in your jurisdiction. You are responsible for maintaining the confidentiality of login credentials and for all activities under your account; immediately notify us of unauthorized access.
          </p>

          <h2>4. Pricing, Billing, Usage & Cancellations</h2>
          <p>
            Our Services include subscription fees and usage‑based billing. Subscription fees are non‑refundable once service is delivered unless otherwise stated. You agree to pay usage fees for calls, texts, emails, AI usage and automations beyond included credits. You may cancel at any time by contacting us or via account settings; cancellations must be received at least three business days before renewal to avoid charges. Billing issues must be reported within 10 days of the invoice date.
          </p>

          <h2>5. Fair Use & Acceptable Use</h2>
          <p>
            You must not misuse our platform to send spam, harass others, violate laws or conduct fraud or illegal activities. See our Acceptable Use Policy for details. Violations may result in suspension or termination.
          </p>

          <h2>6. AI & Automation Disclaimer</h2>
          <p>
            AI and automation features are designed to assist your business; outcomes may vary, and we do not guarantee results. You remain responsible for reviewing AI‑generated content and ensuring compliance with laws. Beta or experimental features are provided “as is,” without warranties.
          </p>

          <h2>7. Service Availability</h2>
          <p>
            We strive for 99.9% uptime but do not guarantee uninterrupted service. Services may be unavailable due to maintenance, third‑party outages (e.g., Twilio, AWS) or other unforeseen issues.
          </p>

          <h2>8. Data Ownership, Creative Work &amp; Intellectual Property</h2>
          <p>
            <strong>Your Business Data.</strong> All data you upload, create or input into the platform, including contacts, customer lists, form submissions and analytics, remains your property at all times. Frayze does not sell, license or share your business data with third parties without your explicit permission, except as required to deliver the Services or comply with applicable law.
          </p>
          <p>
            <strong>Frayze‑Owned Creative Work Product.</strong> All websites, web designs, graphic designs, custom code, copywriting, branding materials, creative assets, marketing content and any other deliverables produced by Frayze Technologies Inc. on your behalf (collectively, "Creative Work Product") are and shall remain the exclusive property of Frayze Technologies Inc. at all times. Frayze retains all rights, title and interest, including all intellectual property rights, in and to all Creative Work Product, regardless of whether you have provided input, direction, content or feedback during the creation process.
          </p>
          <p>
            <strong>License to Use.</strong> While your account is active and all invoices are paid in full, Frayze grants you a limited, non‑exclusive, non‑transferable, revocable license to use the Creative Work Product solely for your internal business purposes. This license does not constitute a transfer of ownership. You may not resell, redistribute, sublicense, reproduce, modify, reverse‑engineer, claim authorship of, or create derivative works from any Creative Work Product without the prior written consent of Frayze Technologies Inc.
          </p>
          <p>
            <strong>License Termination.</strong> Your license to use any Creative Work Product terminates immediately and automatically upon: (a) cancellation or termination of your account for any reason; (b) non‑payment or an overdue balance exceeding 30 days; or (c) breach of these Terms. Upon license termination, you must immediately cease all use of the Creative Work Product, remove it from any websites, platforms or materials under your control, and destroy any copies in your possession. Frayze reserves the right to disable, take down or reclaim deployed Creative Work Product at any time following license termination.
          </p>
          <p>
            <strong>No Transfer of Ownership.</strong> For the avoidance of doubt, no Creative Work Product is sold, assigned or transferred to you at any point. Full payment of invoices entitles you to a continued license to use the Creative Work Product as described above. It does not transfer ownership. Frayze Technologies Inc. may reuse, repurpose, display or reference Creative Work Product (including in portfolios, case studies and marketing materials) at its sole discretion.
          </p>
          <p>
            <strong>Data Export &amp; Portability.</strong> While your account is in good standing, you may request an export of your business data (contacts, form submissions, analytics) at any time. We will provide such data in a commonly used, machine‑readable format within a reasonable timeframe. Export requests do not extend to Creative Work Product, source files, design files or custom code, which remain the exclusive property of Frayze.
          </p>
          <p>
            <strong>Data Retention.</strong> Frayze does not sell or share your data without permission. We may access data solely to deliver services and provide support. Upon cancellation, we may retain business data for up to 60 days, after which it will be permanently deleted unless retention is required by law. Creative Work Product is retained indefinitely by Frayze.
          </p>

          <h2>9. Intellectual Property</h2>
          <p>
            All content, code, designs, trademarks, branding and Creative Work Product on our site, platform and produced through our Services are owned exclusively by Frayze Technologies Inc. or our licensors. You may not copy, modify, reproduce, distribute, reverse‑engineer or claim ownership of any Frayze intellectual property, including any Creative Work Product, without prior written consent from Frayze Technologies Inc. Unauthorized use, reproduction or distribution of Frayze‑owned Creative Work Product may result in legal action.
          </p>

          <h2>10. Termination</h2>
          <p>
            We may suspend or terminate accounts for violation of these Terms, non‑payment, misuse of the platform or to protect the platform’s integrity. You may terminate your subscription at any time. Outstanding usage fees and subscriptions must be paid upon termination.
          </p>

          <h2>11. Third‑Party Links & Content</h2>
          <p>
            Our platform contains links to third‑party websites and integrates with third‑party services. We are not responsible for their content, data practices or availability.
          </p>

          <h2>12. Privacy Policy</h2>
          <p>
            Our Privacy Policy forms part of these Terms. By using the Services, you consent to the collection and use of data as described therein.
          </p>

          <h2>13. Changes to Services & Pricing</h2>
          <p>
            We may modify or discontinue services or adjust pricing. We will notify you of changes via website or email. Continued use after changes indicates acceptance.
          </p>

          <h2>14. Governing Law</h2>
          <p>
            These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada, without regard to conflicts of laws principles. Any disputes arising under these Terms shall be resolved in Ontario courts.
          </p>

          <h2>15. Support & Contact</h2>
          <p>
            For support or questions about these Terms, contact <a href="mailto:support@frayze.ca" className="text-primary hover:underline">support@frayze.ca</a> or mail Frayze Technologies Inc., [address].
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Terms;